import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";
import { parseInt } from "lodash";
import { useDebouncedCallback } from "use-debounce";

import IconOnlyButton from "~/components/buttons/IconOnlyButton";
import { SrOnlyLabel } from "~/styles/screenReaderText";
import { scrollToElement } from "~/utilities/helpers";

const Pagination = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: ${({ theme }) => theme.layout.gutter};
`;

const PaginationIcon = styled(IconOnlyButton)`
    margin-left: 5px;
    font-size: 3rem;
    color: ${({ theme, disabled }) =>
        disabled ? theme.palette.grayFour : theme.palette.purple};
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

    &:hover {
        color: ${({ theme, disabled }) =>
            disabled ? theme.palette.grayFour : theme.palette.teal};
    }
`;

const PageSelectWrapper = styled.div`
    display: flex;
    font-weight: 600;
`;

const StyledSelect = styled.select`
    padding: 5px;
    border: ${({ theme }) => `1px solid ${theme.palette.black}`};
    background-color: ${({ theme }) => theme.palette.grayThree};

    &:focus,
    &:focus-visible {
        border: ${({ theme }) => `1px solid ${theme.palette.black}`};
        outline: none;
    }
`;

const PageIndexWrapper = styled.div`
    margin-left: 15px;
`;

export const PAGINATION_AMOUNTS = [25, 50, 100, 150, 200];

const TablePagination = ({
    gotoPage,
    onPageIndexChange,
    previousPage,
    nextPage,
    setPageSize,
    onPageSizeChange = () => {},
    pageIndex = 0,
    pageCount = 0,
    pageSize = 25,
    pageOptions = [],
    tableOptions = {},
    canPreviousPage = false,
    canNextPage = false,
    scrollToRef = null,
    tableWrapperRef,
}) => {
    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value));

        if (
            tableOptions?.manualPagination &&
            typeof onPageSizeChange === "function"
        ) {
            onPageSizeChange(parseInt(e.target.value));
        }
    };

    const scrollTable = () => {
        if (tableWrapperRef.current) {
            tableWrapperRef.current.scrollTop =
                -tableWrapperRef.current.scrollHeight;
        }
    };

    const scroll = useDebouncedCallback((element, offset) => {
        scrollToElement(element, offset);
        scrollTable();
    }, 200);

    return (
        <Pagination>
            <div>
                <PaginationIcon
                    icon="angle-double-left"
                    iconLibrary="fas"
                    screenReaderText="Go To First Page"
                    onClick={() => {
                        gotoPage(0);
                        onPageIndexChange(0);
                        scroll(scrollToRef, 150);
                    }}
                    disabled={!canPreviousPage}
                />
                <PaginationIcon
                    icon="angle-left"
                    iconLibrary="fas"
                    screenReaderText="Go To Previous Page"
                    onClick={() => {
                        previousPage();
                        onPageIndexChange(pageIndex - 1);
                        scroll(scrollToRef, 150);
                    }}
                    disabled={!canPreviousPage}
                />
            </div>
            <PageSelectWrapper>
                <SrOnlyLabel htmlFor="tableSize">{`Showing ${pageSize} items per page`}</SrOnlyLabel>
                <StyledSelect
                    id="tableSize"
                    value={pageSize}
                    onChange={handlePageSizeChange}
                >
                    {PAGINATION_AMOUNTS.map((pageSize) => (
                        <option key={pageSize} value={pageSize}>
                            Show {pageSize}
                        </option>
                    ))}
                </StyledSelect>
                <PageIndexWrapper>
                    Page {pageIndex + 1} of {pageOptions.length}
                </PageIndexWrapper>
            </PageSelectWrapper>
            <div>
                <PaginationIcon
                    icon="angle-right"
                    iconLibrary="fas"
                    screenReaderText="Go To Next Page"
                    onClick={() => {
                        nextPage();
                        onPageIndexChange(pageIndex + 1);
                        scroll(scrollToRef, 150);
                    }}
                    disabled={!canNextPage}
                />
                <PaginationIcon
                    icon="angle-double-right"
                    iconLibrary="fas"
                    screenReaderText="Go To Last Page"
                    onClick={() => {
                        gotoPage(pageCount - 1);
                        onPageIndexChange(pageCount - 1);
                        scroll(scrollToRef, 150);
                    }}
                    disabled={!canNextPage}
                />
            </div>
        </Pagination>
    );
};

export default TablePagination;

TablePagination.propTypes = {
    gotoPage: PropTypes.func.isRequired,
    onPageIndexChange: PropTypes.func.isRequired,
    previousPage: PropTypes.func.isRequired,
    nextPage: PropTypes.func.isRequired,
    setPageSize: PropTypes.func.isRequired,
    onPageSizeChange: PropTypes.func,
    tableRefWrapper: PropTypes.object,
    pageIndex: PropTypes.number,
    pageCount: PropTypes.number,
    pageSize: PropTypes.number,
    pageOptions: PropTypes.array,
    tableOptions: PropTypes.shape({}),
    canPreviousPage: PropTypes.bool,
    canNextPage: PropTypes.bool,
    scrollToRef: PropTypes.oneOfType([PropTypes.object, PropTypes.element]),
};
